import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { EidosConfigService } from '@app/core/config/eidos-config.service';
import { calculateValues } from './eidos-address-autocomplete.model';

@Component({
  selector: 'eidos-address-autocomplete',
  standalone:true,
  imports:[FormsModule, ReactiveFormsModule],
  templateUrl: './eidos-address-autocomplete.component.html',
  styleUrls: ['./eidos-address-autocomplete.component.scss']
})
export class EidosAddressAutocompleteComponent implements AfterViewInit, OnDestroy {
  @ViewChild('addressInstance', { static:true }) instance?:ElementRef<HTMLInputElement>;
  @ViewChild('divContainer', { static:true }) instanceDivContainer?:ElementRef<HTMLDivElement>;

  @Input() clearDataOnChange:boolean = true;
  @Input() changedBySameAddress:boolean = false;
  @Input() allowZip4:boolean = true;
  @Input() address:any;

  @Output() addressChange = new EventEmitter<any>();
  @Output() addressLong = new EventEmitter<string>();
  @Output() latitude = new EventEmitter<number>();
  @Output() longitude = new EventEmitter<number>();
  @Output() city = new EventEmitter<string>();
  @Output() zip = new EventEmitter<string>();
  @Output() zip4 = new EventEmitter<string>();
  @Output() state = new EventEmitter<string>();
  @Output() county = new EventEmitter<string>();
  @Output() itemAddress = new EventEmitter();

  @Input() set disabled(v:boolean) {
    this.disableTrigger(v);
  }

  @Input() countryIso3?:string;

  private readonly autocompleteFields: string[] = ['address_components', 'geometry', 'name', 'formatted_address', 'place_id'];
  @Input() set country(value:string) {
    this._country = (value ?? '').trim() || 'US';
    void this.initializeAutocomplete();
  }

  @Input() searchType:'establishment' | 'address' | 'geocode' = 'address';

  private autocomplete:google.maps.places.Autocomplete | undefined = undefined;
  private placeChangedListener?: google.maps.MapsEventListener;
  private _country:string = 'US';
  private static googleMapsLoader?: Promise<void>;

  constructor(private readonly eidosConfigService: EidosConfigService) {}

  ngAfterViewInit(): void {
    void this.initializeAutocomplete();
  }

  ngOnDestroy(): void {
    this.autocomplete?.unbindAll();
    this.placeChangedListener?.remove();
  }

  private disableTrigger(v:boolean) {
    if(v) {
      this.instanceDivContainer?.nativeElement.classList.add('dx-state-disabled');
      this.instance!.nativeElement.readOnly = true;
    } else {
      this.instanceDivContainer?.nativeElement.classList.remove('dx-state-disabled');
      this.instance!.nativeElement.readOnly = false;
    }
  }

  private async initializeAutocomplete() {
    try {
      await this.ensureGoogleMapsLoaded();
      this.loadGoogleModule();
    } catch (error) {
      console.warn('Google Maps script not loaded', error);
    }
  }

  private ensureGoogleMapsLoaded(): Promise<void> {
    if ((window as any)?.google?.maps?.places) return Promise.resolve();

    if (!EidosAddressAutocompleteComponent.googleMapsLoader) {
      const key = this.eidosConfigService.currentConfig.getValue().googleMapsApiKey
        ?? (window as any)?.eidosConfiguration?.googleMapsApiKey
        ?? '';

      if (!key) {
        return Promise.reject(new Error('Missing googleMapsApiKey in eidos configuration'));
      }

      EidosAddressAutocompleteComponent.googleMapsLoader = new Promise<void>((resolve, reject) => {
        const scriptId = 'eidos-google-maps-script';
        const existingScript = document.getElementById(scriptId) as HTMLScriptElement | null;

        if (existingScript) {
          if ((window as any)?.google?.maps?.places) {
            resolve();
            return;
          }

          existingScript.addEventListener('load', () => resolve(), { once: true });
          existingScript.addEventListener('error', () => reject(new Error('Failed to load Google Maps script')), { once: true });
          return;
        }

        const script = document.createElement('script');
        script.id = scriptId;
        script.async = true;
        script.defer = true;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places`;
        script.addEventListener('load', () => resolve(), { once: true });
        script.addEventListener('error', () => reject(new Error('Failed to load Google Maps script')), { once: true });
        document.head.appendChild(script);
      });
    }

    return EidosAddressAutocompleteComponent.googleMapsLoader;
  }

  private loadGoogleModule() {
    if (!(window as any)?.google?.maps?.places || !this.instance?.nativeElement) return;
    const countryRestriction = this.getCountryRestriction();

    if(this.autocomplete) {
      if(!this.changedBySameAddress && this.clearDataOnChange)
        this.clearData();
      if (countryRestriction) {
        this.autocomplete.setComponentRestrictions({ country: countryRestriction });
      }
    } else {
      this.autocomplete = new google.maps.places.Autocomplete(this.instance!.nativeElement,{
        componentRestrictions: countryRestriction ? { country: countryRestriction } : undefined,
        fields: this.autocompleteFields,
        types: [this.searchType]
      });
    }

    const load_address_calculate = async () => {
      const place = await this.getResolvedPlace(this.autocomplete?.getPlace());
      if(!place || (!place.name && !place.formatted_address)) {
        this.clearData();
      } else {
        const value = calculateValues(place, this._country, this.allowZip4);
        this.address = value.address;
        this.addressChange.emit(this.address);
        this.addressLong.emit(this.address);
        this.latitude.emit(value.latitude);
        this.longitude.emit(value.longitude);
        this.city.emit(value.city);
        this.zip.emit(value.zip);
        if(this.allowZip4) this.zip4.emit(value.zip4);
        this.county.emit(value.county);
        this.state.emit(value.state);
        this.itemAddress.emit(value);
      }
    }

    this.placeChangedListener?.remove();
    this.placeChangedListener = google.maps.event.addListener(this.autocomplete, 'place_changed', load_address_calculate);
  }

  private getCountryRestriction() {
    const country = this._country.toUpperCase().trim();
    return country.length === 2 ? country : undefined;
  }

  private async getResolvedPlace(place?: google.maps.places.PlaceResult): Promise<google.maps.places.PlaceResult | undefined> {
    const placeId = place?.place_id;
    if (!placeId) return place;
    if (place.geometry && place.address_components?.length) return place;

    return new Promise((resolve) => {
      const detailsService = new google.maps.places.PlacesService(document.createElement('div'));
      detailsService.getDetails(
        {
          placeId,
          fields: this.autocompleteFields,
        },
        (details, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && details) {
            resolve(details);
            return;
          }

          resolve(place);
        },
      );
    });
  }


  private clearData() {
    this.address = null;
    this.addressLong.emit(this.address);
    this.state.emit(undefined);
    this.county.emit(undefined);
    this.city.emit(undefined);
    this.zip.emit(undefined);
    this.longitude.emit(undefined);
    this.latitude.emit(undefined);
    this.itemAddress.emit({
      address: undefined,
      state: undefined,
      county: undefined,
      city: undefined,
      zip: undefined,
      zip4: undefined,
      longitude: undefined,
      latitude: undefined
    });
  }

}
